import { gql, useQuery } from "@apollo/client";
import { Card, CircularProgress, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";
import SoftBox from "components/CheckBox";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatDate } from "utils/formatters";

const PARTIAL_CLOSE_STATE = "Cerrado con entrega parcial";
const COMPLETED_WITH_PARTIAL_DELIVERY_STATE = "Completada con entrega parcial";
const COMPLETED_WITHOUT_DELIVERIES_STATE = "Completada sin entregas";

const getEstadoLines = (status) => {
  switch (status) {
    case PARTIAL_CLOSE_STATE:
      return ["Cerrado", "con", "Entrega parcial"];
    case COMPLETED_WITH_PARTIAL_DELIVERY_STATE:
      return ["Completada", "con", "Entrega parcial"];
    case COMPLETED_WITHOUT_DELIVERIES_STATE:
      return ["Completada", "sin", "entregas"];
    default:
      return [status];
  }
};

export default function ModalSelectProvision({ open, handleClose, setSelection, solicitarPliego }) {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const timeoutID = useRef(null);
  const searchRef = useRef(null);

  const { data, loading } = useQuery(gql`
    query GET_PROVISIONESModalSelectProvision($filter: JSON, $search: String) {
      provisiones(filter: $filter, orderFromToday: true, search: $search, pagination: { page: 1, limit: 20 }) {
        id
        total
        anulada
        fechaVencimiento
        estado
        total
        pedidoCompra{
          id
          nombre
          detalles {
            id
            idMateriaPrima
            requierePack
            unidadPack
          }
        }
        proveedor {
          id
          razonSocial
          nombre
          apellido
          documento
        }
        detalles{
          id
          cantidad
          cantidadEntregada
          precioUnitario
          idPedidoCompraDetalle
          materiaPrimaRecepcion{
            id
            codigo
            tipo
            categoria
            nombre
            idMateriaPrimaReferencia
          }
          materiaPrima{
            id
            codigo
            tipo
            categoria
            nombre
            nombreCompra
            disabled
            idMateriaPrimaReferencia
            tipoUnidad {
              nombre 
              multiplicador
            }
          }
        }
      }
    }
  `, {
    variables: {
      filter: {
        anulada: false,
        _pedidoCompra: {
          _and: [
            { tipoDetalle: "Productos" },
            ...(solicitarPliego ? [{ pliegoRequiereDemanda: "SI" }] : [])
          ]
        }
      },
      search
    },
    skip: !open,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (open) {
      if (searchRef.current) searchRef.current.value = "";
    } else {
      setSearch("");
      setSelected(null);
    }
  }, [open]);

  const handleSelect = (item) => {
    setSelected(prev => prev?.id !== item?.id ? item : null);
  };

  const getEstadoColor = (status) => {
    switch (status) {
      case "Pendiente":
        return "success";
      case "Vencida":
        return "error";
      default:
        return "info";
    }
  };

  const handleSearch = (e) => {
    clearTimeout(timeoutID.current);

    const timeID = setTimeout(() => {
      setSearch(e.target.value);
    }, 1000);

    timeoutID.current = timeID;
  };

  return (<Modal
    open={open}
    onClose={handleClose}
    closeAfterTransition
  >
    <Fade in={open}>
      <Card sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxHeight: "90%",
        width: { xs: "90%", sm: "70%", xxl: "40%" },
        overflow: "auto",
        p: 3,
      }}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="h6">Seleccionar orden de provisión</SoftTypography>
          <Tooltip title="Cerrar" placement="top">
            <SoftButton
              circular
              iconOnly
              color="error"
              variant="gradient"
              onClick={handleClose}
            >
              <Icon>close</Icon>
            </SoftButton>
          </Tooltip>
        </SoftBox>

        <SoftBox my={1}>
          <SoftInput
            ref={searchRef}
            name="search"
            onChange={handleSearch}
            placeholder="Buscar"
            icon={{ component: "search", direction: "left" }}
            fullWidth
          />
        </SoftBox>

        <SoftBox display="flex" flexDirection="column" gap={2} overflow="scroll" maxHeight="30rem" p={1}>
          {loading && (<SoftBox display="flex" justifyContent="center" alignItems="center" height="30rem">
            <CircularProgress size={32} color="uproPink" />
          </SoftBox>)}
          {!loading && data?.provisiones.map((item) => (
            <SoftBox key={item.id}>
              <Card
                sx={{
                  outline: selected?.id === item.id ? `${getUproColor("uproPink")} solid 2px` : "transparent solid 2px",
                  cursor: "pointer",
                  transition: "all 0.5s",
                }}
                onClick={() => handleSelect(item)}
              >
                <Grid container p={1} fontSize="small">
                  <Grid item xs={2}>
                    <div><b>#{item.id} {item.proveedor?.razonSocial ?? `${item.proveedor?.nombre} ${item.proveedor?.apellido}`}</b></div>
                    <div>{item.proveedor?.documento}</div>
                  </Grid>
                  <Grid item xs={3} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <SoftBadge
                      variant="contained"
                      size="sm"
                      badgeContent={(
                        <SoftBox component="span" display="flex" flexDirection="column" gap="2px">
                          {getEstadoLines(item.estado).map((line) => <span key={line}>{line}</span>)}
                        </SoftBox>
                      )}
                      color={getEstadoColor(item.estado)}
                    />
                    <div>{formatDate(item.fechaVencimiento, "dd/MM/yyyy")}</div>
                  </Grid>
                  <Grid item xs={5}>
                    <div><b>#{item.pedidoCompra.id} {item.pedidoCompra.nombre}</b></div>
                  </Grid>
                  <Grid item xs={2} display="flex" justifyContent="end">
                    <Tooltip title="Ver PDF" placement="top">
                      <SoftButton
                        circular
                        iconOnly
                        color="uproPink"
                        variant="gradient"
                        onClick={() => window.open(`/op/pdf/${item?.id ?? 0}`)}
                      >
                        <Icon>file_open</Icon>
                      </SoftButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Card>
            </SoftBox>
          ))}
        </SoftBox>

        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="caption" color="secondary">{selected && `#${selected.id} seleccionado`}</SoftTypography>
          <SoftButton
            variant="gradient"
            color="primary"
            circular
            disabled={!selected}
            onClick={() => {
              setSelection(selected);
              handleClose();
            }}
          >
            <Icon>done</Icon>
            Seleccionar
          </SoftButton>
        </SoftBox>

      </Card>
    </Fade>
  </Modal>)
};

ModalSelectProvision.defaultProps = {
  open: false,
  handleClose: () => { },
  setSelection: () => { },
};

ModalSelectProvision.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  setSelection: PropTypes.func,
}
